import { useState } from 'react';
import { Form, Formik } from 'formik';
import dayjs from 'dayjs';
import { useParams } from 'react-router-dom';
import { Textarea } from '@tag/tag-components-react-v4';

import { GetContractDetails } from '@/graphql/contracts/_generated_/getContractDetails.query';
import { UpdateContractInput } from '../../hooks/useUpdateContract';
import { validationSchema } from './validation';
import styles from './headers.module.scss';
// eslint-disable-next-line import/no-named-as-default
import ValidationAlert from '@/components/common/ValidationAlert/ValidationAlert';
import AgConfirmationModal from '@/components/common/ModalWindow/AgConfirmationModal';
import ContractHeaderPillsItem from '@/components/common/ContractHeaderItem/ContractHeaderPillsItem';
import ContractHeaderDatePickerItem from '@/components/common/ContractHeaderItem/ContractHeaderDatePickerItem';
import ContractHeaderInputItem from '@/components/common/ContractHeaderItem/ContractHeaderInputItem';
import { NumberUtils } from '@/utils/numbers.utils';
import { MAX_DATABASE_CHARS_LENGTH, MAX_ID_CHARS_LENGTH } from '@/config/common.config';
import { FormattingUtils } from '@/utils/Formatting.utils';
import { VARIABLE_TYPE } from '@/config/statuses.config';
import useGetVariableContractDetails from '@/hooks/contract/useGetVariableContractDetails.hook';
import useGetFilteredBCSalesOrders from '../../hooks/useGetPaginatedFilteredBCSalesOrders';
import {
  DateFilterTypes,
  FieldVerificationTypeEnum,
  SalesOrderFilterInput,
  SalesOrderTypePascalCaseEnum
} from '@/graphql/contracts/_generated_/contracts';
import { getWarningMessageForEarlierEndDate, getWarningMessageForSucessfulSOSent } from
  './generateContractEndDateUpdateMessage';
import VerificationTooltipIcon from '@/components/common/ContractHeaderItem/VerificationTooltipIcon';
import { FieldVerificationMap } from '../../hooks/useGetLatestFieldVerifications';
import { User } from '@/hooks/identity/responseModels/user';

interface IHeadersProps {
  isLoading: boolean;
  contract: GetContractDetails['contract'] | undefined;
  onSubmit: (updatedContractData: UpdateContractInput, cb?: () => void) => void;
  isDisabledEditField: boolean;
  fieldVerificationMap?: FieldVerificationMap;
  verificationUsers?: User[];
}

const contractHeaders: Record<string, string> = {
  contractStartDate: 'Contract Start Date',
  contractEndDate: 'Contract End Date',
  renewableTerm: 'Renewable Term',
  externalContractID: 'External Contract Id',
  initialTerm: 'Initial Term'
};

export default function Headers({
  isLoading,
  contract,
  onSubmit,
  isDisabledEditField,
  fieldVerificationMap,
  verificationUsers,
}: IHeadersProps) {
  const renderVerificationIcon = (dbFieldName: string, title?: string) => {
    const verification = fieldVerificationMap?.[dbFieldName];
    if (!verification || verification.type === FieldVerificationTypeEnum.UNVERIFY) return null;

    const user = verificationUsers?.find((u) => u.Id === verification.userID);
    const userName = user
      ? `${user.FirstName ?? ''} ${user.LastName ?? ''}`.trim() || 'Unknown'
      : 'Loading...';

    return (
      <VerificationTooltipIcon
        userName={userName}
        verifiedAt={verification.verifiedAt}
        title={title}
      />
    );
  };

  let totalContractValue: string = '';
  let totalBillingPeriodValue: string = '';
  let predictedAnnualVarBillingValue: string = '';
  let lastAnnualVarBillingValue: string = '';
  const { contractId }: { contractId: UUID } = useParams();
  const { variableContract, isVariableContractLoading } = useGetVariableContractDetails(contractId);
  const { getFilteredBCSalesOrders: getBCSalesOrders, isLoadingGetBCSalesOrders } = useGetFilteredBCSalesOrders();
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [updateSource, setUpdateSource] = useState<'endDate' | undefined>(undefined);
  const [confirmationEndDateMessage, setConfirmationEndDateMessage] = useState<JSX.Element | null>(null);
  const [confirmationInitialTermMessage, setInitialTermConfirmationMessage] = useState<boolean>(false);
  const [updatedContractData, setUpdatedContractData] = useState<UpdateContractInput>({
    contractStartDate: contract?.contractStartDate,
    contractEndDate: contract?.contractEndDate,
  });
  const databaseName = contract?.clientDatabase?.name ?? (contract?.clientDatabaseReference || '');
  const variableBillingTypeOrMix = contract?.billingType.includes(VARIABLE_TYPE);
  const variableTypeOnly = contract?.billingType.length === 1 && contract?.billingType[0] === VARIABLE_TYPE;

  const modalTitle = Object.keys(updatedContractData).find((key) => {
    return updatedContractData[key as keyof UpdateContractInput] !== undefined;
  }) as string;
  if (contract) {
    totalContractValue += `${contract.currency.currency}`;
    totalContractValue += ` ${NumberUtils.prettifyWithRound(contract.totalContractValue)}`;
    totalBillingPeriodValue += `${contract.currency.currency}`;
    totalBillingPeriodValue += ` ${NumberUtils.prettifyWithRound(contract.totalBillingPeriodValue)}`;
    predictedAnnualVarBillingValue += `${contract.currency.currency}`;
    // eslint-disable-next-line max-len
    predictedAnnualVarBillingValue += ` ${NumberUtils.prettifyWithRound(variableContract?.predictedAnnualVarBillingValue)}`;
    lastAnnualVarBillingValue += `${contract.currency.currency}`;
    lastAnnualVarBillingValue += ` ${NumberUtils.prettifyWithRound(variableContract?.lastAnnualVarBillingValue)}`;
  }

  const updateContractData = (updatedData: UpdateContractInput) => {
    setUpdatedContractData(updatedData);
    setShowConfirmationModal(true);
  };

  const checkSuccessfulSO = async (newDate: Date): Promise<boolean> => {
    const currentDate = new Date();
    const start = dayjs(newDate).isBefore(currentDate, 'date') ? newDate : currentDate;
    const end = dayjs(newDate).isAfter(currentDate, 'date') ? newDate : currentDate;

    const salesOrdersSent = await getBCSalesOrders({
      filter:
        {
          contractNumber: { eq: contract?.contractNumber },
          invoiceDate: {
            advanced: {
              type: DateFilterTypes.Between,
              startDate: start,
              endDate: end
            }
          },
          type: {
            eq: SalesOrderTypePascalCaseEnum.SalesOrder
          },
          status: {
            formattedStatus: {
              eq: 'Sent'
            }
          }
        } as SalesOrderFilterInput, first: 1
    });

    if (salesOrdersSent?.items && salesOrdersSent.items.length > 0) {
      return true;
    }
    return false;
  };

  const handleNewEndDateChecks = (value: Date) => {
    const endDateLessThanStartDate = dayjs(value).isBefore(contract?.contractStartDate, 'date');

    if (!endDateLessThanStartDate) {

      const valueIsBefore = dayjs(value).isBefore(new Date(), 'date');

      if (valueIsBefore) {
        setUpdateSource('endDate');
      }

      const valueIsBeforeAndRenewableGreaterThanZero = valueIsBefore &&
        contract?.renewableTerm && contract?.renewableTerm > 0;

      if (valueIsBeforeAndRenewableGreaterThanZero) {
        setConfirmationEndDateMessage(() => getWarningMessageForEarlierEndDate(contract?.initialTermPassed));
      }

      setShowConfirmationModal(true);
      setUpdatedContractData({
        contractEndDate: value
      });
    }
  };

  const submitUpdateContractData = (resetForm: (() => void) | undefined, isInitialForm?: boolean) => {
    if (isInitialForm) {
      setInitialTermConfirmationMessage(false);
    }
    onSubmit(updatedContractData, resetForm);
    setUpdateSource(undefined);
    setShowConfirmationModal(false);
    setConfirmationEndDateMessage(null);
  };

  return (
    <>
      <Formik
        initialValues={{
          renewableTerm: contract?.renewableTerm,
          contractStartDate: contract?.contractStartDate,
          contractEndDate: contract?.contractEndDate,
          externalContractID: contract?.externalContractID ?? '',
          initialTerm: contract?.term ?? 0
        }}
        enableReinitialize={true}
        validationSchema={validationSchema}
        onSubmit={() => {
          return; //handling submit in a diff way
        }}
        validateOnMount={true}
      >
        {({ errors, values, touched, setFieldValue, setFieldTouched, resetForm }) => (
          <>
            <AgConfirmationModal
              isOpen={showConfirmationModal && !isLoadingGetBCSalesOrders}
              onClose={() => {
                setUpdateSource(undefined);
                setShowConfirmationModal(false);
                setUpdatedContractData({
                  contractStartDate: contract?.contractStartDate,
                  contractEndDate: contract?.contractEndDate,
                });
                resetForm();
                setConfirmationEndDateMessage(null);
                setInitialTermConfirmationMessage(false);
              }}
              closeOnConfirm={false}
              onConfirm={async () => {
                let hasSOs = false;
                if (updateSource === 'endDate') {
                  setUpdateSource(undefined);
                  hasSOs = await checkSuccessfulSO(updatedContractData.contractEndDate);

                  if (hasSOs) {
                    setConfirmationEndDateMessage(() =>
                      getWarningMessageForSucessfulSOSent(contract?.initialTermPassed));
                  }
                  else {
                    submitUpdateContractData(resetForm);
                  }
                }
                else {
                  submitUpdateContractData(resetForm);
                }
              }}
              title={`You are about to modify the ${contractHeaders[modalTitle]} for the Contract`}
            >
              {confirmationEndDateMessage ?? (
                <>
                  {!confirmationInitialTermMessage && (
                    <>
                      You are about to modify the {contractHeaders[modalTitle]} for the Contract.
                      <br />
                      This change could potentially impact Sales Order generation and DNR Dates (if applicable).
                      {contract?.initialTermPassed && (
                        <>
                          <br />
                          The contract's initial term has already passed, contract lines won't be affected.
                        </>
                      )}
                      <br />
                    </>
                  )}
                  Please confirm you wish to continue.
                </>
              )}
            </AgConfirmationModal>
            <Form>
              <div className={styles['contract-header-contrainer']}>

                <div>
                  <div className={styles['header-group']}>
                    <ContractHeaderInputItem
                      isLoading={isLoading}
                      label="Customer"
                      value={contract?.customer.customerName}
                      disabled={true}
                      onSubmit={onSubmit}
                      inputStyle={{ width: '100%', marginBottom: '5px' }}
                      labelSuffix={renderVerificationIcon('Customer')}
                    />
                    <div className={styles['header-group']}>
                      <ContractHeaderInputItem
                        isLoading={isLoading}
                        label="Account Code"
                        value={contract?.customer.customerAccountNumber}
                        disabled={true}
                        inputStyle={{ width: '100%' }}
                        labelSuffix={renderVerificationIcon('Account Code')}
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <div className={styles['header-group']}>
                    <span className={styles['contract-label']}>Address{renderVerificationIcon('Address')}</span>
                    <Textarea
                      name='Address'
                      autoComplete='Address'
                      className={styles['address-textarea']}
                      value={
                        [
                          contract?.customer?.address,
                          contract?.customer?.town,
                          contract?.customer?.state,
                          contract?.customer?.postCode,
                          contract?.customer?.country,
                        ]
                          .filter(Boolean)
                          .join('\n')
                      }
                      disabled={true}
                      resize='none'
                      wrap='off'
                    />
                  </div>
                </div>

                <div>
                  <div className={styles['header-group']}>
                    <ContractHeaderInputItem
                      isLoading={isLoading}
                      label="Contract Number"
                      value={contract?.contractNumber}
                      disabled={true}
                      inputStyle={{ width: '100%' }}
                      labelSuffix={renderVerificationIcon('Contract Number')}
                    />
                  </div>
                  <div className={styles['header-group']}>
                    <ContractHeaderInputItem
                      isLoading={isLoading || isVariableContractLoading}
                      label={variableTypeOnly ? 'Predicted Annual VB Value' : 'Total Value'}
                      value={variableTypeOnly ? predictedAnnualVarBillingValue : totalContractValue}
                      disabled={true}
                      inputStyle={{ width: '100%', textAlign: 'right' }}
                      loadingLabel={true}
                      labelSuffix={(
                        <>
                          {renderVerificationIcon('Currency', 'Currency')}
                          {renderVerificationIcon('Total Value', 'Total Value')}
                        </>
                      )}
                    />
                  </div>
                </div>

                <div className={styles['header-group']}>
                  <div className={styles.combined}>
                    <div className="mr-15">
                      <ContractHeaderDatePickerItem
                        value={new Date(values?.contractStartDate)}
                        isLoading={isLoading}
                        name="contractStartDate"
                        label="Start Date"
                        disabled={isDisabledEditField}
                        labelSuffix={renderVerificationIcon('Contract Start Date')}
                        onChange={({ value }) => {
                          if (!value || dayjs(value).isSame(contract?.contractStartDate, 'day')) {
                            return;
                          }
                          setFieldValue('contractStartDate', new Date(value));
                          setFieldTouched('contractStartDate', true, false);

                          if (!dayjs(value)
                            .isAfter(
                              values.contractEndDate ??
                              updatedContractData.contractEndDate ??
                              contract?.contractEndDate, 'date')) {
                            setShowConfirmationModal(true);
                            setUpdatedContractData({
                              contractStartDate: value
                            });

                          }
                        }}
                      />
                      {errors.contractStartDate && touched.contractStartDate && (
                        <ValidationAlert isFloating={true}>{errors.contractStartDate}</ValidationAlert>
                      )}
                    </div>
                    <div>
                      <ContractHeaderDatePickerItem
                        value={new Date(values?.contractEndDate)}
                        isLoading={isLoading}
                        name="contractEndDate"
                        label="End Date"
                        disabled={isDisabledEditField}
                        labelSuffix={renderVerificationIcon('Contract End Date')}
                        onChange={({ value }) => {
                          if (!value || dayjs(value).isSame(contract?.contractEndDate, 'date')) {
                            return;
                          }
                          handleNewEndDateChecks(value);
                          setFieldValue('contractEndDate', new Date(value));
                          setFieldTouched('contractEndDate', true, false);
                        }}
                      />
                      {errors.contractEndDate && touched.contractEndDate && (
                        <ValidationAlert isFloating={true}>{errors.contractEndDate}</ValidationAlert>
                      )}
                    </div>
                  </div>
                  <div className={styles['header-group']}>
                    <ContractHeaderDatePickerItem
                      isLoading={isLoading}
                      label="Rollover Date"
                      value={contract?.rolloverDate ? new Date(contract?.rolloverDate) : undefined}
                      disabled={true}
                      labelSuffix={renderVerificationIcon('Rollover Date')}
                    />
                  </div>
                </div>

                <div>
                  <div className={styles['header-group']}>
                    <div className={styles.combined}>
                      <div>
                        <ContractHeaderInputItem
                          isLoading={isLoading}
                          label="Initial Term (Months)"
                          value={values?.initialTerm}
                          disabled={isDisabledEditField}
                          type='number'
                          name="initialTerm"
                          labelSuffix={renderVerificationIcon('Initial Term')}
                          errorMessage={touched && errors.initialTerm}
                          onSubmit={({ initialTerm }) => {
                            if (initialTerm === contract?.term) {
                              return;
                            }
                            setInitialTermConfirmationMessage(true);
                            updateContractData({ initialTerm });
                          }}

                          onChange={(e) => {
                            setFieldValue('initialTerm', e.target.value);
                            setFieldTouched('initialTerm', true, false);
                          }}
                          inputStyle={{ width: '100%', textAlign: 'right' }}
                        />
                        {errors.initialTerm && touched.initialTerm && (
                          <ValidationAlert isFloating={false}>{errors.initialTerm}</ValidationAlert>
                        )}
                      </div>
                      <div>
                        <ContractHeaderInputItem
                          isLoading={isLoading}
                          label="Renewable Term (Months)"
                          value={values?.renewableTerm}
                          labelSuffix={renderVerificationIcon('Renewable Term')}
                          onSubmit={({ renewableTerm, contractStartDate, contractEndDate }: UpdateContractInput) => {
                            if (renewableTerm === contract?.renewableTerm) {
                              return;
                            }
                            updateContractData({ renewableTerm, contractStartDate, contractEndDate });
                          }}
                          disabled={isDisabledEditField}
                          type='number'
                          name="renewableTerm"
                          errorMessage={touched && errors.renewableTerm}
                          onChange={(e) => {
                            setFieldValue('renewableTerm', e.target.value);
                            setFieldTouched('renewableTerm', true, false);
                          }}
                          inputStyle={{ width: '100%', textAlign: 'right' }}

                        />
                        {errors.renewableTerm && touched.renewableTerm && (
                          <ValidationAlert isFloating={false}>{errors.renewableTerm}</ValidationAlert>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className={styles['header-group']}>
                    <ContractHeaderInputItem
                      isLoading={isLoading}
                      label="Calculated Term"
                      value={contract?.calculatedTerm}
                      disabled={true}
                      inputStyle={{ width: '100%' }}
                      labelSuffix={renderVerificationIcon('Calculated Term')}
                    />
                  </div>
                </div>

                <div>
                  <div className={styles['header-group']}>
                    {variableBillingTypeOrMix && (
                      <div className={styles['header-group']}>
                        <ContractHeaderInputItem
                          isLoading={isLoading}
                          label="External Contract ID"
                          name="externalContractID"
                          value={values.externalContractID}
                          disabled={isDisabledEditField}
                          inputStyle={{ width: '100%' }}
                          title={
                            FormattingUtils.getTitleIfValueIsTrimmed(
                              values.externalContractID,
                              MAX_ID_CHARS_LENGTH
                            )
                          }
                          errorMessage={touched && errors.externalContractID}
                          onChange={({ value }: { value: string }) => {
                            setFieldValue('externalContractID', value.trim() ? value : '');
                            setFieldTouched('externalContractID', true, false);
                          }}
                          onSubmit={({ externalContractID }) => {
                            if (externalContractID === contract?.externalContractID) {
                              return;
                            }
                            updateContractData({ externalContractID });
                          }}
                          labelSuffix={renderVerificationIcon('External Contract ID')}
                        />
                        {errors.externalContractID && touched.externalContractID && (
                          <ValidationAlert isFloating={false}>{errors.externalContractID}</ValidationAlert>
                        )}
                      </div>)}
                    <div className={styles['header-group']}>
                      <ContractHeaderInputItem
                        isLoading={isLoading}
                        label="Database"
                        value={FormattingUtils.getValueTrimmed(databaseName, MAX_DATABASE_CHARS_LENGTH)}
                        disabled={true}
                        inputStyle={{ width: '100%' }}
                        title={FormattingUtils.getTitleIfValueIsTrimmed(databaseName, MAX_DATABASE_CHARS_LENGTH)}
                        labelSuffix={renderVerificationIcon('Database')}
                      />
                    </div>
                    <div className={styles['header-group']}>
                      <ContractHeaderInputItem
                        isLoading={isLoading}
                        label="Origin"
                        value={contract?.origin ?? ''}
                        disabled={true}
                        inputStyle={{ width: '100%' }}
                        labelSuffix={renderVerificationIcon('Origin')}
                      />
                    </div>
                  </div>
                </div>

                <div className={styles['header-group']}>
                  <ContractHeaderPillsItem
                    isLoading={isLoading}
                    label="Product Family"
                    pills={contract?.productFamily ?? []}
                    labelSuffix={renderVerificationIcon('Product Family')}
                  />
                </div>

                {variableTypeOnly && (<div className={styles['header-group']}>
                  <ContractHeaderInputItem
                    isLoading={isVariableContractLoading}
                    label="Last 12-month value"
                    value={lastAnnualVarBillingValue}
                    disabled={true}
                    inputStyle={{ width: '100%' }}
                    loadingLabel={true}
                  />
                </div>)}

                {!variableBillingTypeOrMix && (<div className={styles['header-group']}>
                  <ContractHeaderInputItem
                    isLoading={isLoading}
                    label="Total Billing Period Value"
                    value={totalBillingPeriodValue}
                    disabled={true}
                    inputStyle={{ width: '100%' }}
                    loadingLabel={true}
                  />
                </div>)}

              </div>
            </Form>
          </>
        )}
      </Formik >

    </>
  );
}
